import { useEffect, useState } from 'react';
import type { Me } from '../lib/api';

/**
 * WxCC Migration — TSB / Utility Services UCCX→WxCC cutover.
 * Drives migration-svc through the gateway: shows the connector health (CUCM/UCCX
 * reached through the LB Cloudflare Tunnel), the ordered runbook, and lets a
 * tenant-admin run each step (destructive steps require an explicit confirm).
 */
const API = (import.meta as any).env?.VITE_API_BASE ?? 'https://api.trinethub.com';

interface StepDef { key: string; order: number; phase: 'A' | 'B'; title: string; destructive: boolean; binding: string }
interface Connector { connector: string; status: string; cucm_reachable: number; cucm_version: string; uccx_reachable: number; phones: number; detail: string; last_checked_at: number }

// ── Migration data feeds — the canonical workbook → step → CSV mapping the
//    TSB cutover runs on. Headers are the exact canonical_templates/* schemas.
interface Feed { sheet: string; step: string; phase: 'A' | 'B'; csv: string; stepKey: string; headers: string }
const FEEDS: Feed[] = [
  { sheet: 'Numbers',                          step: 'A1',      phase: 'A', csv: 'numbers',                  stepKey: 'wxc_numbers',     headers: 'phone_number_e164,location,number_type,connection_type,route_group' },
  { sheet: 'Users',                            step: 'A2 / B3', phase: 'A', csv: 'users',                    stepKey: 'wxc_users',       headers: 'First Name,Last Name,Display Name,User ID/Email (Required),Extension,Phone Number,Location,Webex Calling VAR Professional,Webex Contact Center Premium,Webex Contact Center Standard' },
  { sheet: 'Devices',                          step: 'A3',      phase: 'A', csv: 'devices',                  stepKey: 'wxc_devices',     headers: 'mac_address,model,owner_email,is_workspace,location' },
  { sheet: 'Firmware',                         step: 'A4',      phase: 'A', csv: 'firmware',                 stepKey: 'firmware',        headers: 'device_name,model_family,phone_load_name,load_server' },
  { sheet: 'VirtualLines',                     step: 'A5',      phase: 'A', csv: 'virtual_lines',            stepKey: 'virtual_ext',     headers: 'First Name,Last Name,Display Name,Location,Phone Number,Extension' },
  { sheet: 'VirtualLineAssign',                step: 'A5',      phase: 'A', csv: 'virtual_line_assignment',  stepKey: 'virtual_ext',     headers: 'user_email,user_extension,virtual_line_extension,location,assignment_status' },
  { sheet: 'UccxTriggers',                     step: 'B1',      phase: 'B', csv: 'uccx_triggers_to_delete',  stepKey: 'uccx_trigger',    headers: 'trigger_directory_number,uccx_app_name,uccx_location,department,owner_email,deletion_status' },
  { sheet: 'EntryPoints',                      step: 'B2',      phase: 'B', csv: 'wxcc_entry_points',        stepKey: 'wxcc_entrypoint', headers: 'entry_point_name,description,channel_type,active,phone_numbers_e164,extension,location,flow_name' },
  { sheet: 'WxccFlows',                        step: 'B2',      phase: 'B', csv: 'wxcc_flows',               stepKey: 'wxcc_entrypoint', headers: 'flow_name,template_id,languages,queue_names,after_hours_menu,esb_subflow,callback_enabled' },
  { sheet: 'Calabrio',                         step: 'B4',      phase: 'B', csv: 'calabrio_users',           stepKey: 'calabrio_import', headers: 'email,display_name,role,team,skills,license,recording_enabled' },
];
function downloadTemplate(f: Feed) {
  const blob = new Blob([f.headers + '\r\n'], { type: 'text/csv;charset=utf-8' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a'); a.href = url; a.download = `${f.csv}_blank_template.csv`;
  document.body.appendChild(a); a.click(); a.remove(); URL.revokeObjectURL(url);
}

// Minimal RFC-4180-ish CSV parser (handles quoted fields, embedded commas/quotes,
// and CRLF). Returns one object per data row keyed by the header row. No deps.
function parseCsv(text: string): Record<string, string>[] {
  const rows: string[][] = [];
  let cur: string[] = [], field = '', inQ = false;
  for (let i = 0; i < text.length; i++) {
    const c = text[i];
    if (inQ) {
      if (c === '"') { if (text[i + 1] === '"') { field += '"'; i++; } else inQ = false; }
      else field += c;
    } else if (c === '"') inQ = true;
    else if (c === ',') { cur.push(field); field = ''; }
    else if (c === '\n') { cur.push(field); rows.push(cur); cur = []; field = ''; }
    else if (c === '\r') { /* ignore */ }
    else field += c;
  }
  if (field.length || cur.length) { cur.push(field); rows.push(cur); }
  const nonEmpty = rows.filter((r) => r.some((x) => x.trim() !== ''));
  if (nonEmpty.length < 2) return []; // header only (or empty) → no data rows
  const headers = nonEmpty[0].map((h) => h.trim());
  return nonEmpty.slice(1).map((r) => {
    const o: Record<string, string> = {};
    headers.forEach((h, idx) => { o[h] = (r[idx] ?? '').trim(); });
    return o;
  });
}

async function get<T>(path: string): Promise<T> {
  const r = await fetch(`${API}${path}`, { credentials: 'include' });
  if (!r.ok) throw new Error(`${r.status}`);
  return r.json();
}
async function post<T>(path: string, body: unknown): Promise<T> {
  const r = await fetch(`${API}${path}`, { method: 'POST', credentials: 'include', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(body) });
  return r.json();
}
function ago(ms: number) { const s = Math.floor((Date.now() - ms) / 1000); if (s < 90) return `${s}s ago`; const m = Math.floor(s / 60); return m < 90 ? `${m}m ago` : `${Math.floor(m / 60)}h ago`; }

export default function Migration({ me }: { me: Me }) {
  const [runbook, setRunbook] = useState<StepDef[]>([]);
  const [conn, setConn] = useState<Connector | null>(null);
  const [busy, setBusy] = useState<string | null>(null);
  const [results, setResults] = useState<Record<string, string>>({});
  // Filled templates keyed by canonical CSV name (feed.csv).
  const [feedRows, setFeedRows] = useState<Record<string, { fileName: string; rows: Record<string, string>[] }>>({});
  const [uploadErr, setUploadErr] = useState<Record<string, string>>({});
  // Per-step verification result. Run is gated behind a successful verify.
  const [verified, setVerified] = useState<Record<string, { ok: boolean; msg: string }>>({});
  const runId = 'mig-longbeach';
  const canRun = me.role === 'super_admin' || me.role === 'tenant_admin';

  // Re-uploading or clearing a template invalidates any prior verification for
  // every step that feed maps to — you must re-verify before running.
  function invalidateVerify(csv: string) {
    setVerified((m) => { const n = { ...m }; FEEDS.filter((x) => x.csv === csv).forEach((x) => delete n[x.stepKey]); return n; });
  }
  function onUpload(f: Feed, file: File) {
    const reader = new FileReader();
    reader.onload = () => {
      try {
        const rows = parseCsv(String(reader.result ?? ''));
        setFeedRows((m) => ({ ...m, [f.csv]: { fileName: file.name, rows } }));
        setUploadErr((m) => { const n = { ...m }; delete n[f.csv]; return n; });
        invalidateVerify(f.csv);
      } catch (e: any) {
        setUploadErr((m) => ({ ...m, [f.csv]: e?.message ?? 'parse failed' }));
      }
    };
    reader.readAsText(file);
  }
  function clearUpload(f: Feed) {
    setFeedRows((m) => { const n = { ...m }; delete n[f.csv]; return n; });
    invalidateVerify(f.csv);
  }

  // Verify a step's uploaded template(s) BEFORE running: schema match, required
  // fields populated, row count, and connector reachability for CUCM/UCCX steps.
  // Purely local + read-only — it never mutates anything.
  function verifyStep(s: StepDef) {
    const feeds = FEEDS.filter((f) => f.stepKey === s.key);
    const problems: string[] = [];
    let totalRows = 0;
    for (const f of feeds) {
      const up = feedRows[f.csv];
      if (!up) { problems.push(`${f.sheet}: no template uploaded`); continue; }
      if (up.rows.length === 0) { problems.push(`${f.sheet}: 0 data rows`); continue; }
      totalRows += up.rows.length;
      const expected = f.headers.split(',').map((h) => h.trim());
      const got = Object.keys(up.rows[0]).filter((k) => k !== '_sheet');
      const missing = expected.filter((h) => !got.includes(h));
      if (missing.length) problems.push(`${f.sheet}: missing column(s) — ${missing.join(', ')}`);
      for (const rc of expected.filter((h) => /required/i.test(h))) {
        const empties = up.rows.filter((r) => !(r[rc] ?? '').trim()).length;
        if (empties) problems.push(`${f.sheet}: ${empties} row(s) missing "${rc}"`);
      }
    }
    if (s.binding === 'spoke') {
      if (!conn) problems.push('LB connector status unknown — refresh the tile above');
      else if (!conn.cucm_reachable && !conn.uccx_reachable) problems.push('LB tunnel: CUCM/UCCX not reachable');
    }
    const ok = problems.length === 0 && (feeds.length === 0 || totalRows > 0);
    const msg = ok
      ? `verified — ${totalRows} row(s) ready${s.destructive ? ' · DESTRUCTIVE' : ''}`
      : problems.join('; ');
    setVerified((m) => ({ ...m, [s.key]: { ok, msg } }));
  }
  // All uploaded rows feeding a given runbook step (a step may have >1 sheet,
  // e.g. wxcc_entrypoint = EntryPoints + WxccFlows). Tag each row with its sheet.
  function rowsForStep(stepKey: string): Record<string, string>[] {
    return FEEDS.filter((f) => f.stepKey === stepKey)
      .flatMap((f) => (feedRows[f.csv]?.rows ?? []).map((r) => ({ ...r, _sheet: f.sheet })));
  }

  const [loadErr, setLoadErr] = useState<string | null>(null);
  const loadConn = () => get<{ connector: Connector | null }>('/api/migration/connector-status')
    .then((r) => { setConn(r.connector); setLoadErr(null); })
    .catch((e: any) => setLoadErr(`Connector status refresh failed: ${e?.message ?? 'network error'} — showing last known state.`));
  useEffect(() => {
    get<{ runbook: StepDef[] }>('/api/migration/runbook').then((r) => setRunbook(r.runbook))
      .catch((e: any) => setLoadErr(`Migration runbook failed to load: ${e?.message ?? 'network error'} — refresh the page.`));
    loadConn();
    const t = setInterval(loadConn, 60_000);
    return () => clearInterval(t);
  }, []);

  async function runStep(s: StepDef) {
    const rows = rowsForStep(s.key);
    // Verify-before-run gate: a step must pass verification first.
    if (!verified[s.key]?.ok) {
      setResults((m) => ({ ...m, [s.key]: 'verify the template first (click Verify)' }));
      return;
    }
    if (s.destructive && !confirm(`"${s.title}" is DESTRUCTIVE and changes live CUCM/UCCX (${rows.length} row(s)). Proceed?`)) return;
    setBusy(s.key);
    try {
      const res: any = await post(`/api/migration/${runId}/step/${s.key}/run`, { confirm: s.destructive, rows });
      setResults((m) => ({ ...m, [s.key]: res.gated ? `gated: ${res.message}` : (res.detail || (res.ok ? 'ok' : 'failed')) }));
    } catch (e: any) { setResults((m) => ({ ...m, [s.key]: `error: ${e.message}` })); }
    finally { setBusy(null); }
  }

  const phaseA = runbook.filter((s) => s.phase === 'A');
  const phaseB = runbook.filter((s) => s.phase === 'B');
  const online = conn?.status === 'online';

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-semibold tracking-tight">WxCC Migration</h1>
        <p className="text-sm text-gray-500">TSB / Utility Services — UCCX → Webex Contact Center cutover · tenant {me.tenant_id}</p>
      </div>

      {loadErr && (
        <div className="rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700 dark:border-red-900 dark:bg-red-900/20 dark:text-red-300">{loadErr}</div>
      )}

      {/* Connector health */}
      <div className="rounded-xl border border-tnh-border dark:border-tnh-border-dark bg-tnh-card dark:bg-tnh-card-dark p-4">
        <div className="flex items-center gap-3 mb-3">
          <span className={`w-3 h-3 rounded-full ${online ? 'bg-green-500' : 'bg-red-500'}`} />
          <span className="font-semibold">LB Connector (Cloudflare Tunnel)</span>
          <span className={`text-[11px] px-2 py-0.5 rounded-full font-semibold ${online ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'}`}>{conn?.status ?? '…'}</span>
          <button onClick={loadConn} className="ml-auto text-xs text-tnh-accent hover:underline">refresh</button>
        </div>
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 text-sm">
          <Metric k="CUCM" v={conn ? (conn.cucm_reachable ? 'reachable' : 'down') : '—'} ok={!!conn?.cucm_reachable} />
          <Metric k="CUCM version" v={conn?.cucm_version || '—'} />
          <Metric k="Phones" v={conn ? conn.phones.toLocaleString() : '—'} />
          <Metric k="UCCX" v={conn ? (conn.uccx_reachable ? 'reachable' : 'down') : '—'} ok={!!conn?.uccx_reachable} />
        </div>
        {conn && <div className="text-[11px] text-gray-400 mt-2">last checked {ago(conn.last_checked_at)}{conn.detail ? ` · ${conn.detail}` : ''}</div>}
      </div>

      {/* Migration data feeds — workbook sheet → step → canonical CSV */}
      <div className="rounded-xl border border-tnh-border dark:border-tnh-border-dark bg-tnh-card dark:bg-tnh-card-dark p-4">
        <div className="flex items-center justify-between mb-1">
          <span className="font-semibold">Migration data feeds</span>
          <span className="text-[11px] text-gray-400">workbook sheet → step → canonical CSV · {FEEDS.length} feeds</span>
        </div>
        <p className="text-[11px] text-gray-400 mb-3">Each runbook step is driven by a canonical CSV. Download the blank template, fill it (or prefill from CUCM/UCCX), upload it here, then Verify &amp; Run the matching step below.</p>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left text-[11px] uppercase tracking-wide text-gray-400">
                <th className="py-1.5 pr-3">Workbook sheet</th>
                <th className="pr-3">Step</th>
                <th className="pr-3">Canonical CSV</th>
                <th className="pr-3">Fields</th>
                <th className="pr-3">Uploaded</th>
                <th className="text-right">Template</th>
              </tr>
            </thead>
            <tbody>
              {FEEDS.map((f) => {
                const up = feedRows[f.csv];
                return (
                <tr key={f.csv} className="border-t border-tnh-border dark:border-tnh-border-dark">
                  <td className="py-2 pr-3 font-medium">{f.sheet}</td>
                  <td className="pr-3">
                    <span className={`text-[11px] px-1.5 py-0.5 rounded-full font-semibold ${f.phase === 'A' ? 'bg-blue-100 text-blue-700 dark:bg-blue-900/40 dark:text-blue-300' : 'bg-purple-100 text-purple-700 dark:bg-purple-900/40 dark:text-purple-300'}`}>{f.step}</span>
                  </td>
                  <td className="pr-3 font-mono text-[12px] text-gray-600 dark:text-gray-300">{f.csv}.csv</td>
                  <td className="pr-3 text-[11px] text-gray-400" title={f.headers}>{f.headers.split(',').length} cols</td>
                  <td className="pr-3 text-[11px] whitespace-nowrap">
                    {uploadErr[f.csv]
                      ? <span className="text-red-600">{uploadErr[f.csv]}</span>
                      : up
                        ? <span className="text-green-600 dark:text-green-400">✓ {up.rows.length} row(s) <button onClick={() => clearUpload(f)} className="ml-1 text-gray-400 hover:text-red-500" title="Clear">✕</button></span>
                        : <span className="text-gray-400">—</span>}
                  </td>
                  <td className="text-right whitespace-nowrap">
                    <button onClick={() => downloadTemplate(f)} className="text-xs text-tnh-accent hover:underline">Download blank ↓</button>
                    {canRun && (
                      <label className="text-xs text-tnh-accent hover:underline cursor-pointer ml-3">
                        Upload filled ↑
                        <input type="file" accept=".csv,text/csv" className="hidden"
                          onChange={(e) => { const file = e.target.files?.[0]; if (file) onUpload(f, file); e.currentTarget.value = ''; }} />
                      </label>
                    )}
                  </td>
                </tr>
              );})}
            </tbody>
          </table>
        </div>
      </div>

      {/* Runbook */}
      {[['Phase A — Users to Webex Calling', phaseA], ['Phase B — UCCX to WxCC', phaseB]].map(([label, steps]: any) => (
        <div key={label}>
          <div className="text-xs uppercase tracking-wider text-gray-400 font-semibold mb-2">{label}</div>
          <div className="space-y-1.5">
            {steps.map((s: StepDef) => {
              const queued = rowsForStep(s.key).length;
              const v = verified[s.key];
              return (
              <div key={s.key} className="flex items-center gap-3 rounded-lg border border-tnh-border dark:border-tnh-border-dark bg-tnh-card dark:bg-tnh-card-dark px-3 py-2">
                <span className="w-6 text-center text-sm text-gray-400">{s.order}</span>
                <div className="min-w-0 flex-1">
                  <div className="text-sm font-medium truncate">{s.title}</div>
                  <div className="text-[11px] text-gray-400">
                    {s.key}
                    {s.destructive && <span className="ml-2 text-red-500 font-semibold">DESTRUCTIVE</span>}
                    <span className="ml-2 text-gray-500">{queued} row(s) queued</span>
                    {v && <span className={`ml-2 ${v.ok ? 'text-green-600 dark:text-green-400' : 'text-amber-600 dark:text-amber-400'}`}>— {v.ok ? '✓ ' : '⚠ '}{v.msg}</span>}
                    {results[s.key] && <span className="ml-2 text-gray-500">— {results[s.key]}</span>}
                  </div>
                </div>
                {canRun && (
                  <div className="flex items-center gap-1.5">
                    <button onClick={() => verifyStep(s)}
                      className="text-xs px-3 py-1 rounded-md font-medium bg-gray-100 dark:bg-gray-800 text-gray-700 dark:text-gray-200 hover:bg-gray-200 dark:hover:bg-gray-700">
                      Verify
                    </button>
                    <button disabled={busy === s.key || !v?.ok} onClick={() => runStep(s)}
                      title={!v?.ok ? 'Verify the template first' : ''}
                      className={`text-xs px-3 py-1 rounded-md font-medium ${s.destructive ? 'bg-red-50 text-red-700 hover:bg-red-100' : 'bg-tnh-accent/10 text-tnh-accent hover:bg-tnh-accent/20'} disabled:opacity-40 disabled:cursor-not-allowed`}>
                      {busy === s.key ? 'running…' : (s.destructive ? 'Run (confirm)' : 'Run')}
                    </button>
                  </div>
                )}
              </div>
            );})}
          </div>
        </div>
      ))}
      {!canRun && <div className="text-xs text-gray-500">You need tenant-admin to run migration steps.</div>}
    </div>
  );
}

function Metric({ k, v, ok }: { k: string; v: string; ok?: boolean }) {
  return (
    <div className="rounded-lg bg-gray-50 dark:bg-gray-800/40 border border-tnh-border dark:border-tnh-border-dark p-2.5">
      <div className="text-[10px] uppercase tracking-wide text-gray-400">{k}</div>
      <div className={`text-base font-semibold ${ok === undefined ? '' : ok ? 'text-green-600' : 'text-red-600'}`}>{v}</div>
    </div>
  );
}
